import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ArrowLeft, ChevronLeft, ChevronRight, Menu, Settings, X } from 'lucide-react'

import { Logo } from '@/components/common/Logo'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/utils/cn'

import { defaultNavigationConfig } from './defaultNavigationConfig'
import { SidebarItem, type SidebarItemProps } from './SidebarItem'
import { useSidebar } from './useSidebar'
// import { id } from 'zod/locales'

export interface SidebarProps {
  /**
   * Configuración personalizada de navegación
   */
  navigationConfig?: SidebarItemProps[]

  /**
   * Módulo actual para determinar qué navegación mostrar
   */
  currentModule?: 'assessments' | 'admin'

  /**
   * Clase CSS adicional
   */
  className?: string

  /**
   * Si debe mostrar el logo
   */
  showLogo?: boolean

  /**
   * Si debe mostrar el botón de colapsar
   */
  showCollapseButton?: boolean
}

export const Sidebar: React.FC<SidebarProps> = ({
  navigationConfig,
  currentModule = 'assessments',
  className,
  showLogo = true,
  showCollapseButton = true
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isConsultant, isAdmin } = useAuth()
  const { isCollapsed, isMobileOpen, toggleCollapse, setMobileOpen } = useSidebar()

  // Determinar qué navegación mostrar
  const baseNavigation = navigationConfig || defaultNavigationConfig[currentModule]

  // Filtrar navegación basada en roles
  const navigation = baseNavigation.filter((item) => {
    // Si es el elemento de usuarios y no es administrador, ocultarlo
    if (item.id === 'admin-users' && !isAdmin()) {
      return false
    }
    return true
  })

  // Cerrar menú móvil al cambiar de ruta
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname, setMobileOpen])

  return (
    <>
      {/* Overlay para móvil */}
      {isMobileOpen && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className="fixed inset-0 bg-gray-900/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-full bg-white border-r border-gray-200',
          'transition-all duration-300 ease-in-out',
          'lg:relative lg:translate-x-0',
          isCollapsed ? 'w-16' : 'w-64',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full',
          className
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between h-14 px-4 border-b border-gray-200 relative">
            {showLogo && (
              <div className={cn('flex items-center', isCollapsed && 'justify-center w-full')}>
                <Logo
                  variant={isCollapsed ? 'icon' : 'full'}
                  className={cn(
                    'h-8 w-auto transition-all duration-300',
                    isCollapsed && 'scale-90'
                  )}
                />
              </div>
            )}

            {/* Botón cerrar en móvil */}
            <button
              className="lg:hidden text-gray-500 hover:text-gray-700"
              onClick={() => setMobileOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>

            {/* Botón colapsar cuando está expandido */}
            {showCollapseButton && !isCollapsed && (
              <button
                className="hidden lg:block text-gray-500 hover:text-gray-700"
                onClick={toggleCollapse}
                aria-label="Colapsar menú lateral"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}

            {/* Botón expandir cuando está colapsado */}
            {showCollapseButton && isCollapsed && (
              <button
                className={cn(
                  'hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10',
                  'bg-white border border-gray-200 rounded-full p-1.5',
                  'hover:bg-gray-50 transition-colors duration-200',
                  'shadow-sm hover:shadow-md',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                )}
                onClick={toggleCollapse}
                aria-label="Expandir menú lateral"
                title="Expandir menú"
              >
                <ChevronRight className="h-4 w-4 text-gray-600" />
              </button>
            )}
          </div>

          {/* Navegación */}
          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <div className="space-y-1">
              {navigation.map((item, index) => (
                <SidebarItem key={item.id || `${item.label}-${index}`} {...item} />
              ))}
            </div>
          </nav>

          {/* Footer con navegación entre módulos */}
          <div className="border-t border-gray-200 p-3">
            {/* Botón de administración si tiene permisos y está en assessments */}
            {currentModule === 'assessments' && isConsultant() && (
              <button
                onClick={() => navigate('/admin')}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg',
                  'text-gray-700 hover:bg-gray-100 transition-colors duration-200',
                  isCollapsed && 'justify-center'
                )}
              >
                <Settings className="h-5 w-5 flex-shrink-0" />
                {!isCollapsed && <span className="text-sm font-medium">Administración</span>}
              </button>
            )}

            {/* Botón de volver si está en admin */}
            {currentModule === 'admin' && (
              <button
                onClick={() => navigate('/dashboard')}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg',
                  'text-gray-700 hover:bg-gray-100 transition-colors duration-200',
                  isCollapsed && 'justify-center'
                )}
              >
                <ArrowLeft className="h-5 w-5 flex-shrink-0" />
                {!isCollapsed && <span className="text-sm font-medium">Volver a Assessments</span>}
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Botón menú móvil */}
      <button
        className="fixed top-2 left-4 z-40 lg:hidden bg-white rounded-lg p-2 shadow-lg"
        onClick={() => setMobileOpen(true)}
        aria-label="Abrir menú"
      >
        <Menu className="h-5 w-5 text-gray-700" />
      </button>
    </>
  )
}
